import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Firebase/firebaseconfig";
import { registrarAuditoria } from "../helpers/auditoria";
import { toJsDate, toFechaSoloLocal } from "../helpers/fechas";

export const PROMESAS_COLLECTION = "promesasPago";
export const GESTIONES_COLLECTION = "gestionesCobranza";

const getActor = () => {
  const user = getAuth().currentUser;
  return {
    responsableUid: user?.uid || null,
    responsableNombre: user?.displayName || user?.email || "Usuario",
  };
};

// ── Estados de promesa ─────────────────────────────────────────────────────
//   "pendiente"     — recién creada, esperando fecha de vencimiento
//   "cumplida"      — el cliente pagó (registra cumplidaPor con pagoId/cajaMov)
//   "incumplida"    — el cliente no cumplió en la fecha pactada
//   "renegociada"   — se creó una nueva promesa que reemplaza ésta (renegociadaPor: nuevoId)
//   "cancelada"     — anulada administrativamente (motivo en notas)
export const PROMESA_ESTADOS = Object.freeze([
  "pendiente", "cumplida", "incumplida", "renegociada", "cancelada",
]);

export const crearPromesaPago = async ({
  clienteId,
  deudaId,
  montoPrometido,
  fechaPromesa,
  notas = "",
  // Si esta promesa nace por renegociación de otra, sus IDs:
  renegociadaDe = null,
}) => {
  const payload = {
    clienteId,
    deudaId,
    montoPrometido: Number(montoPrometido || 0),
    fechaPromesa,
    estado: "pendiente",
    notas,
    renegociadaDe: renegociadaDe || null,
    ...getActor(),
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, PROMESAS_COLLECTION), payload);

  await registrarAuditoria({
    accion: "cobranza.promesas.crear",
    entidadTipo: "promesaPago",
    entidadId: ref.id,
    detalle: `Promesa de pago registrada para deuda ${deudaId}`,
    despues: {
      ...payload,
      creadoEn: "serverTimestamp",
      actualizadoEn: "serverTimestamp",
    },
  });

  return ref.id;
};

export const actualizarPromesaPago = async (promesaId, cambios = {}) => {
  if (!promesaId) throw new Error("Promesa no valida");

  const promesaRef = doc(db, PROMESAS_COLLECTION, promesaId);
  const snap = await getDoc(promesaRef);
  const antes = snap.exists() ? snap.data() : null;

  const payload = {
    ...cambios,
    actualizadoEn: serverTimestamp(),
    actualizadoPor: getActor().responsableUid,
  };

  if (payload.montoPrometido !== undefined) {
    payload.montoPrometido = Number(payload.montoPrometido || 0);
  }

  await updateDoc(promesaRef, payload);

  await registrarAuditoria({
    accion: "cobranza.promesas.editar",
    entidadTipo: "promesaPago",
    entidadId: promesaId,
    antes,
    despues: {
      ...payload,
      actualizadoEn: "serverTimestamp",
    },
    detalle: `Promesa de pago actualizada (${payload.estado || "datos"})`,
  });
};

export const crearGestionCobranza = async ({
  clienteId,
  deudaId,
  tipo,
  resultado,
  comentario = "",
}) => {
  const payload = {
    clienteId,
    deudaId,
    tipo,
    resultado,
    comentario,
    ...getActor(),
    creadoEn: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, GESTIONES_COLLECTION), payload);

  await registrarAuditoria({
    accion: "cobranza.gestiones.crear",
    entidadTipo: "gestionCobranza",
    entidadId: ref.id,
    detalle: `Gestion de cobranza registrada para deuda ${deudaId}`,
    despues: {
      ...payload,
      creadoEn: "serverTimestamp",
    },
  });

  return ref.id;
};

export const escucharPromesasPorDeuda = (deudaId, callback) => {
  const q = query(
    collection(db, PROMESAS_COLLECTION),
    where("deudaId", "==", deudaId),
    orderBy("creadoEn", "desc")
  );

  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  });
};

export const escucharGestionesPorDeuda = (deudaId, callback) => {
  const q = query(
    collection(db, GESTIONES_COLLECTION),
    where("deudaId", "==", deudaId),
    orderBy("creadoEn", "desc")
  );

  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  });
};

export const obtenerResumenOperativo = async (baseDate = new Date()) => {
  const [promesasSnap, gestionesSnap] = await Promise.all([
    getDocs(collection(db, PROMESAS_COLLECTION)),
    getDocs(collection(db, GESTIONES_COLLECTION)),
  ]);

  const hoy = new Date(baseDate);
  hoy.setHours(0, 0, 0, 0);

  const promesas = promesasSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  const gestiones = gestionesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  const pendientes = promesas.filter((p) => p.estado === "pendiente");
  const incumplidas = pendientes.filter((p) => {
    const fecha = p.fechaPromesa ? toFechaSoloLocal(p.fechaPromesa) : null;
    if (!fecha) return false;
    return fecha < hoy;
  });

  const gestionesHoy = gestiones.filter((g) => {
    const fecha = g.creadoEn?.toDate?.();
    if (!fecha) return false;
    const d = new Date(fecha);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === hoy.getTime();
  });

  return {
    promesasPendientes: pendientes.length,
    promesasIncumplidas: incumplidas.length,
    montoPrometidoPendiente: pendientes.reduce((acc, p) => acc + Number(p.montoPrometido || 0), 0),
    gestionesHoy: gestionesHoy.length,
  };
};

// ── Transiciones de estado de promesa ─────────────────────────────────────

/**
 * Marca una promesa como cumplida. Adjunta opcionalmente la info del pago
 * (pagoId, cajaMovimientoId, monto realmente recibido). El pago debe haberse
 * creado ANTES por el flujo normal (processPayment) — esta función solo
 * cierra la promesa.
 *
 * @param {string} promesaId
 * @param {object} [info]
 * @param {number} [info.montoRecibido]
 * @param {string} [info.pagoId]
 * @param {string} [info.cajaMovimientoId]
 * @param {string} [info.notas]
 */
export const marcarPromesaCumplida = async (promesaId, info = {}) => {
  const payload = {
    estado: "cumplida",
    cumplidaPor: {
      montoRecibido: Number(info.montoRecibido || 0),
      pagoId: info.pagoId || null,
      cajaMovimientoId: info.cajaMovimientoId || null,
      notas: info.notas || "",
      // Usar Date local porque va dentro de un objeto anidado
      fecha: new Date().toISOString(),
      ...getActor(),
    },
    actualizadoEn: serverTimestamp(),
  };
  await updateDoc(doc(db, PROMESAS_COLLECTION, promesaId), payload);
  await registrarAuditoria({
    accion: "cobranza.promesas.cumplir",
    entidadTipo: "promesaPago",
    entidadId: promesaId,
    detalle: `Promesa marcada como cumplida (monto: ${info.montoRecibido || 0})`,
    despues: { ...payload, actualizadoEn: "serverTimestamp" },
  });
};

/**
 * Marca una promesa como incumplida. Crea además una gestión automática
 * de tipo "incumplimiento" para dejar trazabilidad.
 */
export const marcarPromesaIncumplida = async (promesaId, motivo = "") => {
  const pRef = doc(db, PROMESAS_COLLECTION, promesaId);
  const pSnap = await getDoc(pRef);
  if (!pSnap.exists()) throw new Error("Promesa no encontrada.");
  const p = pSnap.data();

  const payload = {
    estado: "incumplida",
    motivoIncumplimiento: motivo || "",
    actualizadoEn: serverTimestamp(),
  };
  await updateDoc(pRef, payload);

  // Gestión automática
  if (p.clienteId && p.deudaId) {
    await crearGestionCobranza({
      clienteId: p.clienteId,
      deudaId: p.deudaId,
      tipo: "incumplimiento",
      resultado: "promesa_incumplida",
      comentario: motivo || `Promesa de ${p.montoPrometido} no cumplida (fecha ${p.fechaPromesa})`,
    }).catch((e) => console.warn("Gestión auto incumplimiento:", e));
  }

  await registrarAuditoria({
    accion: "cobranza.promesas.incumplir",
    entidadTipo: "promesaPago",
    entidadId: promesaId,
    detalle: `Promesa marcada como incumplida: ${motivo || "sin motivo"}`,
    despues: { ...payload, actualizadoEn: "serverTimestamp" },
  });
};

/**
 * Renegociar: crea una nueva promesa que reemplaza la anterior.
 * La anterior queda con estado "renegociada" y referencia la nueva.
 *
 * @param {string} promesaIdAnterior
 * @param {object} datosNuevos  { montoPrometido, fechaPromesa, notas }
 * @returns {Promise<{ nuevaPromesaId: string }>}
 */
export const renegociarPromesa = async (promesaIdAnterior, datosNuevos) => {
  const pRef = doc(db, PROMESAS_COLLECTION, promesaIdAnterior);
  const pSnap = await getDoc(pRef);
  if (!pSnap.exists()) throw new Error("Promesa no encontrada.");
  const p = pSnap.data();

  // Crear la nueva
  const nuevaPromesaId = await crearPromesaPago({
    clienteId: p.clienteId,
    deudaId: p.deudaId,
    montoPrometido: datosNuevos.montoPrometido,
    fechaPromesa: datosNuevos.fechaPromesa,
    notas: datosNuevos.notas || "",
    renegociadaDe: promesaIdAnterior,
  });

  // Cerrar la anterior
  await updateDoc(pRef, {
    estado: "renegociada",
    renegociadaPor: nuevaPromesaId,
    actualizadoEn: serverTimestamp(),
  });

  await registrarAuditoria({
    accion: "cobranza.promesas.renegociar",
    entidadTipo: "promesaPago",
    entidadId: promesaIdAnterior,
    detalle: `Promesa renegociada → ${nuevaPromesaId}`,
    despues: {
      nuevaPromesaId,
      nuevoMonto: datosNuevos.montoPrometido,
      nuevaFecha: datosNuevos.fechaPromesa,
    },
  });

  return { nuevaPromesaId };
};

/**
 * Cancelar una promesa (anulación administrativa, sin marcar incumplimiento).
 */
export const cancelarPromesa = async (promesaId, motivo = "") => {
  const payload = {
    estado: "cancelada",
    motivoCancelacion: motivo || "",
    actualizadoEn: serverTimestamp(),
  };
  await updateDoc(doc(db, PROMESAS_COLLECTION, promesaId), payload);
  await registrarAuditoria({
    accion: "cobranza.promesas.cancelar",
    entidadTipo: "promesaPago",
    entidadId: promesaId,
    detalle: `Promesa cancelada: ${motivo || "sin motivo"}`,
    despues: { ...payload, actualizadoEn: "serverTimestamp" },
  });
};

// ── Listados globales para el Panel de Seguimiento ────────────────────────

/**
 * Lista global de promesas con filtros opcionales. Sin orderBy para evitar
 * índices compuestos; ordena client-side.
 */
export const listarPromesasGlobal = async ({
  estado = null,
  cobradorId = null,
  desde = null,
  hasta = null,
} = {}) => {
  const constraints = [];
  if (estado) constraints.push(where("estado", "==", estado));
  if (cobradorId) constraints.push(where("responsableUid", "==", cobradorId));

  const q = constraints.length
    ? query(collection(db, PROMESAS_COLLECTION), ...constraints)
    : query(collection(db, PROMESAS_COLLECTION));
  const snap = await getDocs(q);
  let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Filtrar por rango de fechaPromesa client-side si se especificó
  if (desde || hasta) {
    items = items.filter((p) => {
      const f = p.fechaPromesa;
      if (!f) return false;
      if (desde && f < desde) return false;
      if (hasta && f > hasta) return false;
      return true;
    });
  }

  items.sort((a, b) => {
    const ta = a.creadoEn?.toMillis?.() ?? 0;
    const tb = b.creadoEn?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return items;
};

/**
 * Lista global de gestiones con filtros opcionales.
 */
export const listarGestionesGlobal = async ({
  cobradorId = null,
  desde = null,
  hasta = null,
} = {}) => {
  const constraints = [];
  if (cobradorId) constraints.push(where("responsableUid", "==", cobradorId));

  const q = constraints.length
    ? query(collection(db, GESTIONES_COLLECTION), ...constraints)
    : query(collection(db, GESTIONES_COLLECTION));
  const snap = await getDocs(q);
  let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (desde || hasta) {
    const desdeMs = desde ? new Date(`${desde}T00:00:00`).getTime() : 0;
    const hastaMs = hasta ? new Date(`${hasta}T23:59:59`).getTime() : Infinity;
    items = items.filter((g) => {
      const ms = g.creadoEn?.toMillis?.() ?? 0;
      return ms >= desdeMs && ms <= hastaMs;
    });
  }

  items.sort((a, b) => {
    const ta = a.creadoEn?.toMillis?.() ?? 0;
    const tb = b.creadoEn?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return items;
};
